import React, { useEffect, useState } from "react";
import { PopulateClientDropDownAsync } from "../../Services/ClientService";
import { getLearnerOverviewAsync } from "../../Services/ReportService";
import { sendReminderAsync } from "../../Services/AccountService";
import { showToast } from "../../Infrastructure/Notifications/ToastrConfig";
import Table from "../../Shared/Components/Table";
import Card from "../../Shared/Components/Card/Card";
import Header from "../../Shared/Components/Header/Header";
import AiOverviewCard from "./Components/AiOverviewCard";
import * as XLSX from "xlsx";
import { formatDateTime } from "../../Infrastructure/Utilities/DateHelper";

const LeanerOverview = () => {
  const selectionColumnWidth = 70;
  const nameColumnWidth = 220;
  const emailColumnWidth = 260;
  const nameColumnLeft = `${selectionColumnWidth}px`;
  const emailColumnLeft = `${selectionColumnWidth + nameColumnWidth}px`;

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [learners, setLearners] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [groups, setGroups] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [aiOverview, setAiOverview] = useState("");

  const isCompletedStatus = (status) => {
    const statusLower = (status || "").toLowerCase();
    return (
      statusLower.includes("completed") || statusLower.includes("complete")
    );
  };

  const isInProgressStatus = (status) => {
    const statusLower = (status || "").toLowerCase();
    return (
      statusLower.includes("in progress") ||
      statusLower.includes("in-progress") ||
      (statusLower.includes("started") && !statusLower.includes("not"))
    );
  };

  const isNotStartedStatus = (status) => {
    const statusLower = (status || "").toLowerCase();
    return (
      !status ||
      statusLower.includes("not started") ||
      statusLower.includes("not-started")
    );
  };

  const buildLearnerCourseStatusMap = (learner) => {
    const learnerCourseStatus = new Map();
    if (learner.courses && learner.courses.length > 0) {
      learner.courses.forEach((course) => {
        const key = course.courseId || course.id;
        learnerCourseStatus.set(key, course.status);
      });
    }
    return learnerCourseStatus;
  };

  const buildLearnerCourseMap = (learner) => {
    const learnerCourseMap = new Map();
    (learner.courses || []).forEach((course) => {
      learnerCourseMap.set(course.courseId || course.id, course);
    });
    return learnerCourseMap;
  };

  const getExportHeaderRow = (allCourses) => [
    "Learner Name",
    "Learner Email",
    "Group",
    "Registered",
    ...allCourses.flatMap((course) => [
      `${course.name} - Status`,
      `${course.name} - Started`,
      `${course.name} - Completed`,
    ]),
  ];

  const getExportLearnerRow = (learner, allCourses) => {
    const learnerCourseMap = buildLearnerCourseMap(learner);
    return [
      learner.name,
      learner.email || "",
      learner.groupName || "",
      formatDateTime(learner.registeredDateTime, ""),
      ...allCourses.flatMap((course) => {
        const learnerCourse = learnerCourseMap.get(course.id);
        return [
          learnerCourse?.status || "Not Started",
          formatDateTime(learnerCourse?.startedDateTime, ""),
          formatDateTime(learnerCourse?.completedDateTime, ""),
        ];
      }),
    ];
  };

  const learnerHasStatus = (learner, filter, allCourses) => {
    const learnerCourseStatus = buildLearnerCourseStatusMap(learner);

    for (const course of allCourses) {
      const status = learnerCourseStatus.get(course.id) || "Not Started";

      if (filter === "completed" && isCompletedStatus(status)) {
        return true;
      }

      if (filter === "in-progress" && isInProgressStatus(status)) {
        return true;
      }

      if (filter === "not-started" && isNotStartedStatus(status)) {
        return true;
      }
    }

    return false;
  };

  const learnerCompletedAllAssignedCourses = (learner, allCourses) => {
    if (allCourses.length === 0) {
      return false;
    }

    const learnerCourseStatus = buildLearnerCourseStatusMap(learner);
    return allCourses.every((course) => {
      const status = learnerCourseStatus.get(course.id) || "Not Started";
      return isCompletedStatus(status);
    });
  };

  const getLearnerId = (learner) => learner?.id || learner?.userId || null;

  const learnerHasIncompleteCourses = (learner, allCourses) => {
    if (allCourses.length === 0) {
      return false;
    }

    const learnerCourseStatus = buildLearnerCourseStatusMap(learner);
    return allCourses.some((course) => {
      const status = learnerCourseStatus.get(course.id) || "Not Started";
      return !isCompletedStatus(status);
    });
  };

  const getFilterLabel = () => {
    if (statusFilter === "completed-all") {
      return "completed-all";
    }

    return statusFilter;
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await PopulateClientDropDownAsync();
        setClients(response.clients);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };

    fetchClients();
  }, []);

  const handleClientChange = async (evt) => {
    const clientId = evt.target.value;
    setSelectedClientId(clientId);
    setStatusFilter("all"); // Reset filter when changing clients
    setGroupFilter("all");
    setGroups([]);
    setSelectedUserIds([]);

    if (clientId) {
      setLoading(true);
      try {
        const response = await getLearnerOverviewAsync(clientId);
        const fetchedLearners = response.learners || [];
        setLearners(fetchedLearners);
        setAiOverview(response.aiOverview || "");
        // Extract unique groups
        const uniqueGroups = [
          ...new Map(
            fetchedLearners
              .filter((l) => l.groupId && l.groupName)
              .map((l) => [l.groupId, { id: l.groupId, name: l.groupName }]),
          ).values(),
        ];
        setGroups(uniqueGroups);
        // Use courses from response if available, otherwise extract from learners
        if (response.courses && Array.isArray(response.courses)) {
          setCourses(
            response.courses.sort(
              (a, b) => (a.ordinal || 0) - (b.ordinal || 0),
            ),
          );
        } else {
          setCourses([]);
        }
      } catch (error) {
        console.error("Error fetching learner overview:", error);
        setLearners([]);
        setCourses([]);
        setAiOverview("");
      } finally {
        setLoading(false);
      }
    } else {
      setLearners([]);
      setCourses([]);
      setAiOverview("");
    }
  };

  const getEligibleReminderUserIds = () => {
    const allCourses = getAllCourses();
    return getFilteredLearners()
      .filter((learner) => learnerHasIncompleteCourses(learner, allCourses))
      .map(getLearnerId)
      .filter(Boolean);
  };

  const eligibleReminderUserIds = getEligibleReminderUserIds();
  const allEligibleSelected =
    eligibleReminderUserIds.length > 0 &&
    eligibleReminderUserIds.every((id) => selectedUserIds.includes(id));

  const handleToggleLearnerSelection = (learnerId) => {
    if (!learnerId) {
      return;
    }

    setSelectedUserIds((previous) => {
      if (previous.includes(learnerId)) {
        return previous.filter((id) => id !== learnerId);
      }

      return [...previous, learnerId];
    });
  };

  const handleToggleSelectAllEligible = () => {
    setSelectedUserIds((previous) => {
      if (allEligibleSelected) {
        return previous.filter((id) => !eligibleReminderUserIds.includes(id));
      }

      return Array.from(new Set([...previous, ...eligibleReminderUserIds]));
    });
  };

  const handleSendReminders = async () => {
    if (selectedUserIds.length === 0) {
      showToast(false, "Select at least one learner with incomplete progress");
      return;
    }

    setSendingReminders(true);
    try {
      const response = await sendReminderAsync({ userIds: selectedUserIds });
      if (response?.success) {
        showToast(
          true,
          `Reminder email sent to ${selectedUserIds.length} learner${selectedUserIds.length > 1 ? "s" : ""}`,
        );
        setSelectedUserIds([]);
      } else {
        showToast(
          false,
          response?.errors?.[0]?.error || "Failed to send reminder email(s)",
        );
      }
    } catch (error) {
      showToast(false, "Failed to send reminder email(s)");
    } finally {
      setSendingReminders(false);
    }
  };

  // Export table data to CSV
  const exportToCSV = () => {
    const allCourses = getAllCourses();
    const filteredLearners = getFilteredLearners();
    const selectedClient = clients.find((c) => c.id === selectedClientId);
    const clientName = selectedClient ? selectedClient.name : "client";
    const filterLabel = getFilterLabel();

    const toCsvRow = (cells) =>
      cells
        .map((cell) => '"' + String(cell ?? "").replace(/"/g, '""') + '"')
        .join(",");

    // Create CSV header
    let csv = toCsvRow(getExportHeaderRow(allCourses)) + "\n";

    // Add data rows
    filteredLearners.forEach((learner) => {
      csv += toCsvRow(getExportLearnerRow(learner, allCourses)) + "\n";
    });

    // Create blob and download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split("T")[0];

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${clientName}_learner_overview_${filterLabel}_${timestamp}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    const allCourses = getAllCourses();
    const filteredLearners = getFilteredLearners();
    const selectedClient = clients.find((c) => c.id === selectedClientId);
    const clientName = selectedClient ? selectedClient.name : "client";
    const filterLabel = getFilterLabel();
    const timestamp = new Date().toISOString().split("T")[0];

    const worksheetRows = [getExportHeaderRow(allCourses)];

    filteredLearners.forEach((learner) => {
      worksheetRows.push(getExportLearnerRow(learner, allCourses));
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Learner Overview");

    XLSX.writeFile(
      workbook,
      `${clientName}_learner_overview_${filterLabel}_${timestamp}.xlsx`,
    );
  };

  // Get all unique courses - use courses from state if available
  function getAllCourses() {
    // If we have courses from the response, use them (already sorted by ordinal)
    if (courses && courses.length > 0) {
      return courses;
    }

    // Fallback: extract from learners if courses not available in response
    const coursesMap = new Map();
    learners.forEach((learner) => {
      if (learner.courses && learner.courses.length > 0) {
        learner.courses.forEach((course) => {
          if (!coursesMap.has(course.courseId)) {
            coursesMap.set(course.courseId, {
              id: course.courseId,
              name: course.courseName,
              ordinal: course.ordinal || 0,
            });
          }
        });
      }
    });
    // Convert to array and sort by ordinal
    return Array.from(coursesMap.values()).sort(
      (a, b) => a.ordinal - b.ordinal,
    );
  }

  // Filter learners based on status filter and group filter
  function getFilteredLearners() {
    let filtered = learners;

    if (groupFilter !== "all") {
      filtered = filtered.filter((learner) => learner.groupId === groupFilter);
    }

    if (statusFilter === "all") {
      return filtered;
    }

    const allCourses = getAllCourses();

    if (statusFilter === "completed-all") {
      return filtered.filter((learner) =>
        learnerCompletedAllAssignedCourses(learner, allCourses),
      );
    }

    return filtered.filter((learner) =>
      learnerHasStatus(learner, statusFilter, allCourses),
    );
  }

  // Calculate statistics
  const getStats = () => {
    const courses = getAllCourses();
    let totalLearners = learners.length;
    let totalCourseEnrollments = totalLearners * courses.length;
    let completed = 0;
    let inProgress = 0;
    let notStarted = 0;

    learners.forEach((learner) => {
      // Create a map of courseId to status for this learner
      const learnerCourseStatus = new Map();
      if (learner.courses && learner.courses.length > 0) {
        learner.courses.forEach((course) => {
          learnerCourseStatus.set(course.courseId, course.status);
        });
      }

      // Check each course for this learner
      courses.forEach((course) => {
        const status = learnerCourseStatus.get(course.id) || "Not Started";

        if (isNotStartedStatus(status)) {
          notStarted++;
        } else if (isCompletedStatus(status)) {
          completed++;
        } else if (isInProgressStatus(status)) {
          inProgress++;
        } else {
          notStarted++;
        }
      });
    });

    return {
      totalLearners,
      totalCourseEnrollments,
      completed,
      inProgress,
      notStarted,
      completionRate:
        totalCourseEnrollments > 0
          ? Math.round((completed / totalCourseEnrollments) * 100)
          : 0,
    };
  };

  // Get background color based on status
  const getStatusStyle = (status) => {
    if (!status || status === "-" || status === "Not Started") {
      return {
        backgroundColor: "#ffebee",
        color: "#c62828",
        padding: "8px 12px",
        borderRadius: "6px",
        fontWeight: "500",
        fontSize: "0.9rem",
        textAlign: "center",
      };
    }

    const statusLower = status.toLowerCase();

    if (isNotStartedStatus(status)) {
      return {
        backgroundColor: "#ffebee",
        color: "#c62828",
        padding: "8px 12px",
        borderRadius: "6px",
        fontWeight: "500",
        fontSize: "0.9rem",
        textAlign: "center",
      };
    } else if (isInProgressStatus(status)) {
      return {
        backgroundColor: "#fff3e0",
        color: "#e65100",
        padding: "8px 12px",
        borderRadius: "6px",
        fontWeight: "500",
        fontSize: "0.9rem",
        textAlign: "center",
      };
    } else if (isCompletedStatus(status)) {
      return {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
        padding: "8px 12px",
        borderRadius: "6px",
        fontWeight: "500",
        fontSize: "0.9rem",
        textAlign: "center",
      };
    }

    return {
      padding: "8px 12px",
      textAlign: "center",
    };
  };

  const headings = () => {
    const courses = getAllCourses();
    return (
      <>
        <th
          style={{
            width: `${selectionColumnWidth}px`,
            minWidth: `${selectionColumnWidth}px`,
            padding: "16px",
            textAlign: "center",
            position: "sticky",
            left: 0,
            background: "#f8f9fa",
            borderRight: "2px solid #e0e0e0",
            zIndex: 11,
          }}
        >
          <input
            type="checkbox"
            checked={allEligibleSelected}
            onChange={handleToggleSelectAllEligible}
            disabled={eligibleReminderUserIds.length === 0 || sendingReminders}
          />
        </th>
        <th
          style={{
            fontWeight: "600",
            fontSize: "0.9rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "#555",
            padding: "16px",
            position: "sticky",
            left: nameColumnLeft,
            background: "#f8f9fa",
            borderRight: "2px solid #e0e0e0",
            zIndex: 10,
            minWidth: `${nameColumnWidth}px`,
            width: `${nameColumnWidth}px`,
          }}
        >
          Learner Name
        </th>
        <th
          style={{
            fontWeight: "600",
            fontSize: "0.9rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "#555",
            padding: "16px",
            position: "sticky",
            left: emailColumnLeft,
            background: "#f8f9fa",
            borderRight: "2px solid #e0e0e0",
            zIndex: 10,
            minWidth: `${emailColumnWidth}px`,
            width: `${emailColumnWidth}px`,
            boxShadow: "6px 0 8px -6px rgba(0, 0, 0, 0.18)",
          }}
        >
          Learner Email
        </th>
        <th
          style={{
            fontWeight: "600",
            fontSize: "0.9rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "#555",
            padding: "16px",
            background: "#f8f9fa",
            minWidth: "160px",
          }}
        >
          Group
        </th>
        <th
          style={{
            fontWeight: "600",
            fontSize: "0.9rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "#555",
            padding: "16px",
            background: "#f8f9fa",
            minWidth: "160px",
          }}
        >
          Registered
        </th>
        {courses.map((course) => (
          <th
            key={course.id}
            style={{
              fontWeight: "600",
              fontSize: "0.9rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "#555",
              padding: "16px",
              background: "#f8f9fa",
              textAlign: "center",
            }}
          >
            {course.name}
          </th>
        ))}
      </>
    );
  };

  const courseDateStyle = {
    fontSize: "0.75rem",
    fontWeight: "normal",
    whiteSpace: "nowrap",
  };

  const rowData = () => {
    const courses = getAllCourses();
    const filteredLearners = getFilteredLearners();

    if (loading) {
      return (
        <tr>
          <td
            className="no-data"
            colSpan={courses.length + 5}
            style={{
              padding: "60px",
              textAlign: "center",
              fontSize: "1.1rem",
              color: "#666",
            }}
          >
            <div style={{ display: "inline-block" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #667eea",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 16px",
                }}
              ></div>
              Loading learner data...
            </div>
          </td>
        </tr>
      );
    }

    if (learners.length === 0) {
      return (
        <tr>
          <td
            className="no-data"
            colSpan={courses.length + 5}
            style={{
              padding: "60px",
              textAlign: "center",
              fontSize: "1rem",
              color: "#999",
            }}
          >
            {selectedClientId
              ? "📭 No learners found for this client"
              : "👆 Please select a client to view learner data"}
          </td>
        </tr>
      );
    }

    if (filteredLearners.length === 0) {
      return (
        <tr>
          <td
            className="no-data"
            colSpan={courses.length + 5}
            style={{
              padding: "60px",
              textAlign: "center",
              fontSize: "1rem",
              color: "#999",
            }}
          >
            🔍 No learners match the selected status filter
          </td>
        </tr>
      );
    }

    return (
      <>
        {filteredLearners.map((learner) => {
          // Map courseId to this learner's course progress (matches course.id from getAllCourses())
          const learnerCourseMap = buildLearnerCourseMap(learner);

          const learnerId = getLearnerId(learner);
          const canSendReminder =
            learnerHasIncompleteCourses(learner, courses) && !!learnerId;

          return (
            <tr
              key={learner.id}
              style={{ transition: "background-color 0.2s ease" }}
            >
              <td
                style={{
                  width: `${selectionColumnWidth}px`,
                  minWidth: `${selectionColumnWidth}px`,
                  padding: "16px",
                  textAlign: "center",
                  position: "sticky",
                  left: 0,
                  background: "#fff",
                  borderRight: "2px solid #f0f0f0",
                  zIndex: 9,
                }}
              >
                {canSendReminder ? (
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(learnerId)}
                    onChange={() => handleToggleLearnerSelection(learnerId)}
                    disabled={sendingReminders}
                  />
                ) : null}
              </td>
              <td
                style={{
                  fontWeight: "600",
                  color: "#333",
                  fontSize: "0.95rem",
                  padding: "16px",
                  position: "sticky",
                  left: nameColumnLeft,
                  background: "#fff",
                  borderRight: "2px solid #f0f0f0",
                  minWidth: `${nameColumnWidth}px`,
                  width: `${nameColumnWidth}px`,
                }}
              >
                {learner.name}
              </td>
              <td
                style={{
                  color: "#333",
                  fontSize: "0.95rem",
                  padding: "16px",
                  position: "sticky",
                  left: emailColumnLeft,
                  background: "#fff",
                  borderRight: "2px solid #f0f0f0",
                  minWidth: `${emailColumnWidth}px`,
                  width: `${emailColumnWidth}px`,
                  boxShadow: "6px 0 8px -6px rgba(0, 0, 0, 0.18)",
                }}
              >
                {learner.email}
              </td>
              <td
                style={{
                  color: "#555",
                  fontSize: "0.9rem",
                  padding: "16px",
                }}
              >
                {learner.groupName || "-"}
              </td>
              <td
                style={{
                  color: "#555",
                  fontSize: "0.9rem",
                  padding: "16px",
                  whiteSpace: "nowrap",
                }}
              >
                {formatDateTime(learner.registeredDateTime)}
              </td>
              {courses.map((course) => {
                const learnerCourse = learnerCourseMap.get(course.id);
                const status = learnerCourse?.status || "Not Started";
                return (
                  <td
                    key={`${learner.id}-${course.id}`}
                    style={{ padding: "12px", ...getStatusStyle(status) }}
                  >
                    {status}
                    {learnerCourse?.startedDateTime && (
                      <div style={courseDateStyle}>
                        Started: {formatDateTime(learnerCourse.startedDateTime)}
                      </div>
                    )}
                    {learnerCourse?.completedDateTime && (
                      <div style={courseDateStyle}>
                        Completed: {formatDateTime(learnerCourse.completedDateTime)}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </>
    );
  };

  return (
    <>
      <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
      <Header
        heading="Learner Overview"
        subheading="View learners and their course progress"
        icon="users"
      />
      <div className="container-fluid content">
        <Card>
          <div className="row">
            <div className="col-12">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label
                  htmlFor="clientDropdown"
                  style={{
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: "#333",
                    display: "block",
                  }}
                >
                  Select Client
                </label>
                <select
                  id="clientDropdown"
                  className="form-control"
                  value={selectedClientId}
                  onChange={handleClientChange}
                  style={{
                    maxWidth: "400px",
                    padding: "12px 16px",
                    fontSize: "1rem",
                    lineHeight: "1.5",
                    height: "auto",
                    borderRadius: "8px",
                    border: "2px solid #e0e0e0",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <option value="">Select a client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </Card>

        {selectedClientId && learners.length > 0 && (
          <div className="row" style={{ marginTop: "24px" }}>
            <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "16px",
                  padding: "28px",
                  boxShadow: "0 10px 25px rgba(102, 126, 234, 0.25)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  cursor: "pointer",
                  color: "white",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 15px 35px rgba(102, 126, 234, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 25px rgba(102, 126, 234, 0.25)";
                }}
              >
                <div
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    opacity: 0.9,
                    marginBottom: "12px",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  👥 Total Learners
                </div>
                <div
                  style={{
                    fontSize: "3rem",
                    fontWeight: "700",
                    lineHeight: "1",
                  }}
                >
                  {getStats().totalLearners}
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                  borderRadius: "16px",
                  padding: "28px",
                  boxShadow: "0 10px 25px rgba(17, 153, 142, 0.25)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  cursor: "pointer",
                  color: "white",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 15px 35px rgba(17, 153, 142, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 25px rgba(17, 153, 142, 0.25)";
                }}
              >
                <div
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    opacity: 0.9,
                    marginBottom: "12px",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  ✓ Completed
                </div>
                <div
                  style={{
                    fontSize: "3rem",
                    fontWeight: "700",
                    lineHeight: "1",
                  }}
                >
                  {getStats().completed}
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  borderRadius: "16px",
                  padding: "28px",
                  boxShadow: "0 10px 25px rgba(240, 147, 251, 0.25)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  cursor: "pointer",
                  color: "white",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 15px 35px rgba(240, 147, 251, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 25px rgba(240, 147, 251, 0.25)";
                }}
              >
                <div
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    opacity: 0.9,
                    marginBottom: "12px",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  ⏳ In Progress
                </div>
                <div
                  style={{
                    fontSize: "3rem",
                    fontWeight: "700",
                    lineHeight: "1",
                  }}
                >
                  {getStats().inProgress}
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                  borderRadius: "16px",
                  padding: "28px",
                  boxShadow: "0 10px 25px rgba(250, 112, 154, 0.25)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  cursor: "pointer",
                  color: "white",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 15px 35px rgba(250, 112, 154, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 25px rgba(250, 112, 154, 0.25)";
                }}
              >
                <div
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    opacity: 0.9,
                    marginBottom: "12px",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  ○ Not Started
                </div>
                <div
                  style={{
                    fontSize: "3rem",
                    fontWeight: "700",
                    lineHeight: "1",
                  }}
                >
                  {getStats().notStarted}
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedClientId && (
          <div style={{ marginTop: "24px" }}>
            <AiOverviewCard aiOverview={aiOverview} />
            <Card title="Learners and Course Status">
              <div className="row">
                <div className="col-12 mb-3">
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      alignItems: "center",
                    }}
                  >
                    <button
                      className="btn btn-primary"
                      onClick={exportToCSV}
                      disabled={getFilteredLearners().length === 0}
                      style={{
                        padding: "10px 24px",
                        borderRadius: "8px",
                        fontWeight: "600",
                        fontSize: "0.95rem",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        border: "none",
                        cursor:
                          getFilteredLearners().length === 0
                            ? "not-allowed"
                            : "pointer",
                        opacity: getFilteredLearners().length === 0 ? 0.6 : 1,
                      }}
                    >
                      <span>📥</span>
                      Export to CSV
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={exportToExcel}
                      disabled={getFilteredLearners().length === 0}
                      style={{
                        padding: "10px 24px",
                        borderRadius: "8px",
                        fontWeight: "600",
                        fontSize: "0.95rem",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        border: "none",
                        cursor:
                          getFilteredLearners().length === 0
                            ? "not-allowed"
                            : "pointer",
                        opacity: getFilteredLearners().length === 0 ? 0.6 : 1,
                      }}
                    >
                      <span>📊</span>
                      Export to Excel
                    </button>

                    <button
                      className="btn btn-warning"
                      onClick={handleSendReminders}
                      disabled={
                        selectedUserIds.length === 0 || sendingReminders
                      }
                      style={{
                        padding: "10px 24px",
                        borderRadius: "8px",
                        fontWeight: "600",
                        fontSize: "0.95rem",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        border: "none",
                        cursor:
                          selectedUserIds.length === 0 || sendingReminders
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          selectedUserIds.length === 0 || sendingReminders
                            ? 0.6
                            : 1,
                      }}
                    >
                      <span>📧</span>
                      {sendingReminders
                        ? "Sending..."
                        : `Send Reminder${selectedUserIds.length > 1 ? "s" : ""}${selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ""}`}
                    </button>

                    <div
                      style={{
                        display: "inline-flex",
                        gap: "8px",
                        marginLeft: "auto",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      {groups.length > 0 && (
                        <>
                          <span
                            style={{
                              alignSelf: "center",
                              fontWeight: "600",
                              fontSize: "0.9rem",
                              color: "#555",
                              marginRight: "4px",
                            }}
                          >
                            Filter by Group:
                          </span>
                          <select
                            value={groupFilter}
                            onChange={(e) => setGroupFilter(e.target.value)}
                            style={{
                              padding: "8px 14px",
                              borderRadius: "6px",
                              fontWeight: "600",
                              fontSize: "0.9rem",
                              border:
                                groupFilter !== "all"
                                  ? "2px solid #667eea"
                                  : "2px solid #e0e0e0",
                              background:
                                groupFilter !== "all" ? "#667eea" : "white",
                              color: groupFilter !== "all" ? "white" : "#666",
                              cursor: "pointer",
                              marginRight: "16px",
                            }}
                          >
                            <option value="all">All Groups</option>
                            {groups.map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.name}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                      <span
                        style={{
                          alignSelf: "center",
                          fontWeight: "600",
                          fontSize: "0.9rem",
                          color: "#555",
                          marginRight: "8px",
                        }}
                      >
                        Filter by Status:
                      </span>
                      <button
                        onClick={() => setStatusFilter("all")}
                        style={{
                          padding: "8px 20px",
                          borderRadius: "6px",
                          fontWeight: "600",
                          fontSize: "0.9rem",
                          border:
                            statusFilter === "all"
                              ? "2px solid #667eea"
                              : "2px solid #e0e0e0",
                          background:
                            statusFilter === "all" ? "#667eea" : "white",
                          color: statusFilter === "all" ? "white" : "#666",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          boxShadow:
                            statusFilter === "all"
                              ? "0 2px 8px rgba(102, 126, 234, 0.3)"
                              : "none",
                        }}
                      >
                        All
                      </button>
                      <button
                        onClick={() => setStatusFilter("completed")}
                        style={{
                          padding: "8px 20px",
                          borderRadius: "6px",
                          fontWeight: "600",
                          fontSize: "0.9rem",
                          border:
                            statusFilter === "completed"
                              ? "2px solid #2e7d32"
                              : "2px solid #e0e0e0",
                          background:
                            statusFilter === "completed" ? "#2e7d32" : "white",
                          color:
                            statusFilter === "completed" ? "white" : "#666",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          boxShadow:
                            statusFilter === "completed"
                              ? "0 2px 8px rgba(46, 125, 50, 0.3)"
                              : "none",
                        }}
                      >
                        ✓ Completed
                      </button>
                      <button
                        onClick={() => setStatusFilter("completed-all")}
                        style={{
                          padding: "8px 20px",
                          borderRadius: "6px",
                          fontWeight: "600",
                          fontSize: "0.9rem",
                          border:
                            statusFilter === "completed-all"
                              ? "2px solid #1b5e20"
                              : "2px solid #e0e0e0",
                          background:
                            statusFilter === "completed-all"
                              ? "#1b5e20"
                              : "white",
                          color:
                            statusFilter === "completed-all" ? "white" : "#666",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          boxShadow:
                            statusFilter === "completed-all"
                              ? "0 2px 8px rgba(27, 94, 32, 0.3)"
                              : "none",
                        }}
                      >
                        ✅ Completed All Assigned
                      </button>
                      <button
                        onClick={() => setStatusFilter("in-progress")}
                        style={{
                          padding: "8px 20px",
                          borderRadius: "6px",
                          fontWeight: "600",
                          fontSize: "0.9rem",
                          border:
                            statusFilter === "in-progress"
                              ? "2px solid #e65100"
                              : "2px solid #e0e0e0",
                          background:
                            statusFilter === "in-progress"
                              ? "#e65100"
                              : "white",
                          color:
                            statusFilter === "in-progress" ? "white" : "#666",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          boxShadow:
                            statusFilter === "in-progress"
                              ? "0 2px 8px rgba(230, 81, 0, 0.3)"
                              : "none",
                        }}
                      >
                        ⏳ In Progress
                      </button>
                      <button
                        onClick={() => setStatusFilter("not-started")}
                        style={{
                          padding: "8px 20px",
                          borderRadius: "6px",
                          fontWeight: "600",
                          fontSize: "0.9rem",
                          border:
                            statusFilter === "not-started"
                              ? "2px solid #c62828"
                              : "2px solid #e0e0e0",
                          background:
                            statusFilter === "not-started"
                              ? "#c62828"
                              : "white",
                          color:
                            statusFilter === "not-started" ? "white" : "#666",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          boxShadow:
                            statusFilter === "not-started"
                              ? "0 2px 8px rgba(198, 40, 40, 0.3)"
                              : "none",
                        }}
                      >
                        ○ Not Started
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-12" style={{ overflowX: "auto" }}>
                  <div
                    style={{
                      borderRadius: "8px",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Table headings={headings} rowData={rowData} />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
};

export default LeanerOverview;
